import { post as postAuth, get as getAuth, put as putAuth, del as deleteAuth, patch as patchAuth, postImg } from "./axios_auth_api";
import { post as postNonAuth, get as getNonAuth } from "./axios_api";
import { endpoints } from "helpers/endpoints";


export const postLogin = async (data: { userName: string; password: string }) =>
  postNonAuth(endpoints.login, data);

// Logout - uses non-auth endpoint to handle cases with expired tokens
export const postLogout = async () =>
  postNonAuth(endpoints.logout, {});

// Refresh Token - uses non-auth endpoint
export const postRefreshToken = async (data: {
  accessToken: string;
  refreshToken: string;
}) =>
  postNonAuth(endpoints.refreshToken, data);

// Products API endpoints
export const getProducts = async (params: {
  CategoryId?: number;
  MinPrice?: number;
  MaxPrice?: number;
  IsActive?: boolean;
  SupplierId?: number;
  PageSize: number;
  PageIndex: number;
  SearchTerm?: string;
  SortBy?: string;
  IsDescending?: boolean;
}) => {
  const queryParams = new URLSearchParams();

  // Add each parameter if it exists
  if (params.CategoryId !== undefined) queryParams.append('CategoryId', params.CategoryId.toString());
  if (params.MinPrice !== undefined) queryParams.append('MinPrice', params.MinPrice.toString());
  if (params.MaxPrice !== undefined) queryParams.append('MaxPrice', params.MaxPrice.toString());
  if (params.IsActive !== undefined) queryParams.append('IsActive', params.IsActive.toString());
  if (params.SupplierId !== undefined) queryParams.append('SupplierId', params.SupplierId.toString());
  if (params.PageSize !== undefined) queryParams.append('PageSize', params.PageSize.toString());
  if (params.PageIndex !== undefined) queryParams.append('PageIndex', params.PageIndex.toString());
  if (params.SearchTerm) queryParams.append('SearchTerm', params.SearchTerm);
  if (params.SortBy) queryParams.append('SortBy', params.SortBy);
  if (params.IsDescending !== undefined) queryParams.append('IsDescending', params.IsDescending.toString());

  return getAuth(endpoints.getProducts + "?" + queryParams.toString());
};

export const getProductById = async (id: number) =>
  getAuth(endpoints.getProductById(id));

// Specification type for product specifications
export interface ProductSpecification {
  specNameEn: string;
  specNameAr: string;
  specValue: string;
  unit: string;
  displayOrder: number;
  isHighlighted: boolean;
}

export const createProduct = async (data: {
  enName: string;
  arName: string;
  enDescription?: string;
  arDescription?: string;
  price: number;
  categoryId: number;
  brand?: string;
  internalCode?: string;
  stock?: number;
  lowStockThreshold?: number;
  isBundle?: boolean;
  couponGenerationMode?: string;
  imageUrl?: string;
  unit?: string;
  isActive: boolean;
  isPinned?: boolean;
  pinnedOrder?: number;
  specifications?: ProductSpecification[];
  availableQuantity: number;
}) => postAuth(endpoints.createProduct, data);

export const updateProduct = async (id: number, data: {
  enName: string;
  arName: string;
  enDescription?: string;
  arDescription?: string;
  price: number;
  categoryId: number;
  brand?: string;
  internalCode?: string;
  stock?: number;
  lowStockThreshold?: number;
  isBundle?: boolean;
  couponGenerationMode?: string;
  imageUrl?: string;
  unit?: string;
  isActive: boolean;
  isPinned?: boolean;
  pinnedOrder?: number;
  specifications?: ProductSpecification[];
  availableQuantity: number;
}) => putAuth(endpoints.updateProduct(id), data);

export const deleteProduct = async (id: number) =>
  deleteAuth(endpoints.deleteProduct(id));

// Attach existing document as product image
export const attachProductImage = async (productId: number, documentId: number, isPrimary: boolean) =>
  postAuth(endpoints.attachProductImage(productId, isPrimary), documentId);

// Upload base64 image and link to product
export const uploadProductImage = async (productId: number, data: {
  base64Image: string;
  caption: string;
  isMain: boolean;
}) => postAuth(endpoints.uploadProductImage(productId), data);

// Update product inventory by terminal
export const updateProductInventory = async (productId: number, items: {
  terminalId: number;
  quantity: number;
}[]) => putAuth(endpoints.updateProductInventory(productId), { items });

// Product Categories API endpoints
export const getAllProductCategories = async () =>
  getAuth(endpoints.getAllProductCategories);

export const getProductCategoryById = async (id: number) =>
  getAuth(endpoints.getProductCategoryById(id));

export const createProductCategory = async (data: {
  aR_NAME: string;
  eN_NAME: string;
  uI_ORDER_ID: number;
  parenT_ID: number | null;
  iS_ACTIVE: boolean;
}) => postAuth(endpoints.createProductCategory, data);

export const updateProductCategory = async (id: number, data: {
  aR_NAME: string;
  eN_NAME: string;
  uI_ORDER_ID: number;
  parenT_ID: number | null;
  iS_ACTIVE: boolean;
}) => putAuth(endpoints.updateProductCategory(id), data);

export const deleteProductCategory = async (id: number, mode?: string, moveToCategoryId?: number) =>
  deleteAuth(endpoints.deleteProductCategory(id, mode, moveToCategoryId));

export const toggleProductCategoryStatus = async (id: number) =>
  patchAuth(endpoints.toggleProductCategoryStatus(id), {});

export const getProductsByCategoryId = async (id: number) =>
  getAuth(endpoints.getProductsByCategoryId(id));

// Delivery Men API endpoints
export const getDeliveryMen = async () =>
  getAuth(endpoints.getDeliveryMen);

export const getDeliveryManById = async (id: number) =>
  getAuth(endpoints.getDeliveryManById(id));

export const createDeliveryMan = async (data: {
  name: string;
  mobile: string;
  address?: string;
  overview?: string;
  isActive: boolean;
}) => postAuth(endpoints.createDeliveryMan, data);

export const updateDeliveryMan = async (id: number, data: {
  name: string;
  mobile: string;
  address?: string;
  overview?: string;
  isActive: boolean;
}) => putAuth(endpoints.updateDeliveryMan(id), data);

export const deleteDeliveryMan = async (id: number) =>
  deleteAuth(endpoints.deleteDeliveryMan(id));

// Coupons API endpoints
export const getCoupons = async (params: {
  IsActive?: boolean;
  IsExpired?: boolean;
  DiscountTypeId?: number;
  PageSize: number;
  PageIndex: number;
  SearchTerm?: string;
  SortBy?: string;
  IsDescending?: boolean;
}) => {
  const queryParams = new URLSearchParams();

  if (params.IsActive !== undefined) queryParams.append('IsActive', params.IsActive.toString());
  if (params.IsExpired !== undefined) queryParams.append('IsExpired', params.IsExpired.toString());
  if (params.DiscountTypeId !== undefined) queryParams.append('DiscountTypeId', params.DiscountTypeId.toString());
  if (params.PageSize !== undefined) queryParams.append('PageSize', params.PageSize.toString());
  if (params.PageIndex !== undefined) queryParams.append('PageIndex', params.PageIndex.toString());
  if (params.SearchTerm) queryParams.append('SearchTerm', params.SearchTerm);
  if (params.SortBy) queryParams.append('SortBy', params.SortBy);
  if (params.IsDescending !== undefined) queryParams.append('IsDescending', params.IsDescending.toString());

  return getAuth(endpoints.getCoupons + "?" + queryParams.toString());
};

export const getCouponById = async (id: number) =>
  getAuth(endpoints.getCouponById(id));

export const createCoupon = async (data: {
  code: string;
  enName: string;
  arName: string;
  discountValue: number;
  discountTypeId: number;
  expiryDate: string;
  isActive: boolean;
}) => postAuth(endpoints.createCoupon, data);

export const updateCoupon = async (id: number, data: {
  code?: string;
  enName?: string;
  arName?: string;
  discountValue?: number;
  discountTypeId?: number;
  expiryDate?: string;
  isActive?: boolean;
}) => putAuth(endpoints.updateCoupon(id), data);

export const deleteCoupon = async (id: number) =>
  deleteAuth(endpoints.deleteCoupon(id));

// Discount Types API endpoints
export const getDiscountTypes = async () =>
  getAuth(endpoints.getDiscountTypes);

export const createDiscountType = async (data: {
  enName: string;
  arName: string;
}) => postAuth(endpoints.createDiscountType, data);

// Orders API endpoints
export const getOrders = async (params: {
  StatusId?: number;
  FromDate?: string;
  ToDate?: string;
  IssuedByAccountId?: number;
  TerminalId?: number;
  IsPaid?: boolean;
  OrderReference?: string;
  PageSize: number;
  PageIndex: number;
  SearchTerm?: string;
  SortBy?: string;
  IsDescending?: boolean;
}) => {
  const queryParams = new URLSearchParams();

  if (params.StatusId !== undefined) queryParams.append('StatusId', params.StatusId.toString());
  if (params.FromDate !== undefined) queryParams.append('FromDate', params.FromDate);
  if (params.ToDate !== undefined) queryParams.append('ToDate', params.ToDate);
  if (params.IssuedByAccountId !== undefined) queryParams.append('IssuedByAccountId', params.IssuedByAccountId.toString());
  if (params.TerminalId !== undefined) queryParams.append('TerminalId', params.TerminalId.toString());
  if (params.IsPaid !== undefined) queryParams.append('IsPaid', params.IsPaid.toString());
  if (params.OrderReference !== undefined) queryParams.append('OrderReference', params.OrderReference);
  if (params.PageSize !== undefined) queryParams.append('PageSize', params.PageSize.toString());
  if (params.PageIndex !== undefined) queryParams.append('PageIndex', params.PageIndex.toString());
  if (params.SearchTerm) queryParams.append('SearchTerm', params.SearchTerm);
  if (params.SortBy) queryParams.append('SortBy', params.SortBy);
  if (params.IsDescending !== undefined) queryParams.append('IsDescending', params.IsDescending.toString());

  return getAuth(endpoints.getOrders + "?" + queryParams.toString());
};

export const getOrderById = async (id: number) =>
  getAuth(endpoints.getOrderById(id));

export const updateOrderStatus = async (id: number, data: {
  StatusId: number;
  ActionId: number;
  Notes?: string;
}) => postAuth(endpoints.updateOrderStatus(id), data);

export const orderConfirmation = async (id: number, data: {
  notes?: string;
  documentBase64?: string[];
}) => postAuth(endpoints.orderConfirmation(id), data);

// Disputes API endpoints
export const getDisputes = async (params: {
  StatusId?: number;
  IssuedByAccountId?: number;
  FromDate?: string;
  ToDate?: string;
  PageSize: number;
  PageIndex: number;
  SearchTerm?: string;
  SortBy?: string;
  IsDescending?: boolean;
}) => {
  const queryParams = new URLSearchParams();

  if (params.StatusId !== undefined) queryParams.append('StatusId', params.StatusId.toString());
  if (params.IssuedByAccountId !== undefined) queryParams.append('IssuedByAccountId', params.IssuedByAccountId.toString());
  if (params.FromDate !== undefined) queryParams.append('FromDate', params.FromDate);
  if (params.ToDate !== undefined) queryParams.append('ToDate', params.ToDate);
  if (params.PageSize !== undefined) queryParams.append('PageSize', params.PageSize.toString());
  if (params.PageIndex !== undefined) queryParams.append('PageIndex', params.PageIndex.toString());
  if (params.SearchTerm) queryParams.append('SearchTerm', params.SearchTerm);
  if (params.SortBy) queryParams.append('SortBy', params.SortBy);
  if (params.IsDescending !== undefined) queryParams.append('IsDescending', params.IsDescending.toString());

  return getAuth(endpoints.getDisputes + "?" + queryParams.toString());
};

export const getDisputeById = async (id: number) =>
  getAuth(endpoints.getDisputeById(id));

export const createDispute = async (data: {
  title: string;
  items: {
    orderId: number;
    productId: number;
    notes: string;
  }[];
  claims: string;
  documentIds?: number[];
}) => postAuth(endpoints.createDispute, data);

export const updateDispute = async (id: number, data: {
  title: string;
  items: {
    orderId: number;
    productId: number;
    notes: string;
  }[];
  claims: string;
  documentIds?: number[];
}) => putAuth(endpoints.updateDispute(id), data);

export const deleteDispute = async (id: number) =>
  deleteAuth(endpoints.deleteDispute(id));

export const changeDisputeStatus = async (id: number, data: {
  statusId: number;
  actionId: number;
  notes?: string;
  isInternal?: boolean;
}) => postAuth(endpoints.changeDisputeStatus(id), data);

export const addDisputeLog = async (id: number, data: {
  actionId: number;
  notes: string;
  isInternal: boolean;
  documentIds?: number[];
}) => postAuth(endpoints.addDisputeLog(id), data);

export const respondToDispute = async (id: number, data: {
  responseMessage: string;
  attachedDocumentIds?: number[] | null;
}) => postAuth(endpoints.respondToDispute(id), data);

export const rejectDispute = async (id: number, data: {
  rejectionReason: string;
  attachedDocumentIds?: number[];
}) => postAuth(endpoints.rejectDispute(id), data);